import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Checkbox, Slider } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>/?~";
const AMBIGUOUS = new Set("O0oIl1|`'\"");

const MIN_LENGTH = 4;
const MAX_LENGTH = 128;
const MIN_COUNT = 1;
const MAX_COUNT = 50;

const getCrypto = () => {
  if (typeof window !== "undefined" && window.crypto) return window.crypto;
  return null;
};

const secureRandomInt = (maxExclusive) => {
  const cryptoObj = getCrypto();
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const range = 256 - (256 % maxExclusive);
    const buf = new Uint8Array(1);
    while (true) {
      cryptoObj.getRandomValues(buf);
      if (buf[0] < range) return buf[0] % maxExclusive;
    }
  }
  return Math.floor(Math.random() * maxExclusive);
};

const shuffle = (arr) => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = secureRandomInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const buildAlphabet = ({ lower, upper, digits, symbols, excludeAmbiguous }) => {
  let pool = "";
  if (lower) pool += LOWER;
  if (upper) pool += UPPER;
  if (digits) pool += DIGITS;
  if (symbols) pool += SYMBOLS;
  if (excludeAmbiguous) {
    pool = Array.from(pool)
      .filter((c) => !AMBIGUOUS.has(c))
      .join("");
  }
  return pool;
};

const generatePassword = (length, opts) => {
  const pool = buildAlphabet(opts);
  if (!pool) return "";
  const required = [];
  const maybePush = (set) => {
    if (!set) return;
    const filtered = opts.excludeAmbiguous
      ? Array.from(set).filter((c) => !AMBIGUOUS.has(c))
      : Array.from(set);
    if (filtered.length === 0) return;
    required.push(filtered[secureRandomInt(filtered.length)]);
  };
  if (opts.lower) maybePush(LOWER);
  if (opts.upper) maybePush(UPPER);
  if (opts.digits) maybePush(DIGITS);
  if (opts.symbols) maybePush(SYMBOLS);
  const rest = [];
  const total = Math.max(length, required.length);
  for (let i = required.length; i < total; i += 1) {
    rest.push(pool[secureRandomInt(pool.length)]);
  }
  return shuffle(required.concat(rest)).slice(0, total).join("");
};

const estimateEntropy = (length, opts) => {
  const pool = buildAlphabet(opts);
  if (!pool || length <= 0) return 0;
  return Math.round(length * Math.log2(pool.length) * 10) / 10;
};

const strengthBucket = (bits) => {
  if (bits < 36) return "weak";
  if (bits < 60) return "fair";
  if (bits < 90) return "strong";
  return "excellent";
};

const PasswordGenerator = () => {
  const { t } = useTranslation();
  const [length, setLength] = useState(16);
  const [count, setCount] = useState(5);
  const [lower, setLower] = useState(true);
  const [upper, setUpper] = useState(true);
  const [digits, setDigits] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);
  const [maskAll, setMaskAll] = useState(false);
  const [revealed, setRevealed] = useState(() => new Set());
  const [passwords, setPasswords] = useState(() =>
    Array.from({ length: 5 }, () =>
      generatePassword(16, {
        lower: true,
        upper: true,
        digits: true,
        symbols: true,
        excludeAmbiguous: false,
      })
    )
  );

  const opts = useMemo(
    () => ({ lower, upper, digits, symbols, excludeAmbiguous }),
    [lower, upper, digits, symbols, excludeAmbiguous]
  );

  const hasAnyClass = lower || upper || digits || symbols;

  const generate = useCallback(() => {
    setRevealed(new Set());
    if (!hasAnyClass) {
      setPasswords([]);
      return;
    }
    const n = Math.min(MAX_COUNT, Math.max(MIN_COUNT, Number(count) || 1));
    const len = Math.min(MAX_LENGTH, Math.max(MIN_LENGTH, Number(length) || MIN_LENGTH));
    const next = Array.from({ length: n }, () => generatePassword(len, opts));
    setPasswords(next);
  }, [count, length, opts, hasAnyClass]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      generate();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [generate]);

  const toggleRevealed = (idx) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const entropy = useMemo(() => estimateEntropy(length, opts), [length, opts]);
  const strengthKey = useMemo(() => strengthBucket(entropy), [entropy]);
  const strengthColor = {
    weak: "text-red-600 bg-red-100 border-red-200",
    fair: "text-amber-700 bg-amber-100 border-amber-200",
    strong: "text-emerald-700 bg-emerald-100 border-emerald-200",
    excellent: "text-indigo-700 bg-indigo-100 border-indigo-200",
  }[strengthKey];

  const joined = useMemo(() => passwords.join("\n"), [passwords]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">{t("password_gen_title")}</h1>
            <p className="text-gray-600">{t("password_gen_intro")}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("password_gen_length_label")}: <span className="font-mono">{length}</span>
                  </label>
                  <Slider
                    min={MIN_LENGTH}
                    max={MAX_LENGTH}
                    value={length}
                    onChange={setLength}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("password_gen_count_label")}
                  </label>
                  <input
                    type="number"
                    min={MIN_COUNT}
                    max={MAX_COUNT}
                    value={count}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      if (Number.isNaN(n)) {
                        setCount(MIN_COUNT);
                        return;
                      }
                      setCount(Math.min(MAX_COUNT, Math.max(MIN_COUNT, n)));
                    }}
                    className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("password_gen_charset_label")}
                </label>
                <Checkbox checked={lower} onChange={(e) => setLower(e.target.checked)}>
                  {t("password_gen_lowercase")}
                </Checkbox>
                <Checkbox checked={upper} onChange={(e) => setUpper(e.target.checked)}>
                  {t("password_gen_uppercase")}
                </Checkbox>
                <Checkbox checked={digits} onChange={(e) => setDigits(e.target.checked)}>
                  {t("password_gen_digits")}
                </Checkbox>
                <Checkbox checked={symbols} onChange={(e) => setSymbols(e.target.checked)}>
                  {t("password_gen_symbols")}
                </Checkbox>
                <Checkbox
                  checked={excludeAmbiguous}
                  onChange={(e) => setExcludeAmbiguous(e.target.checked)}
                >
                  {t("password_gen_exclude_ambiguous")}
                </Checkbox>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <StandardButton
                onClick={generate}
                type="primary"
                size="middle"
                aria-keyshortcuts="Meta+Enter Control+Enter"
              >
                {t("password_gen_generate")}
              </StandardButton>
              <StandardButton
                onClick={() => copy(joined, t("password_gen_copy_all_label"))}
                type="secondary"
                variant="outlined"
                size="middle"
                disabled={passwords.length === 0}
              >
                {t("password_gen_copy_all")}
              </StandardButton>
              <Checkbox
                checked={maskAll}
                onChange={(e) => setMaskAll(e.target.checked)}
              >
                {t("password_gen_mask_label")}
              </Checkbox>
              <div
                className={`text-xs px-3 py-1 rounded-full border ${strengthColor}`}
                title={`${entropy} bits`}
              >
                {t("password_gen_strength_label")}: {t(`password_gen_strength_${strengthKey}`)}{" "}
                · {entropy} {t("password_gen_bits")}
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-1">
              {t("password_gen_shortcut_hint")}
            </p>

            {!hasAnyClass ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {t("password_gen_need_charset")}
              </div>
            ) : (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <ul className="flex flex-col divide-y divide-indigo-100">
                  {passwords.map((pw, idx) => {
                    const isHidden = maskAll && !revealed.has(idx);
                    const display = isHidden ? "\u2022".repeat(pw.length) : pw;
                    return (
                      <li
                        key={`${idx}-${pw}`}
                        className="flex items-center justify-between gap-3 py-2"
                      >
                        <span
                          className="font-mono text-sm break-all text-gray-800 select-all"
                          aria-label={
                            isHidden
                              ? t("password_gen_password_hidden_aria")
                              : undefined
                          }
                        >
                          {display}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          {maskAll && (
                            <button
                              type="button"
                              onClick={() => toggleRevealed(idx)}
                              aria-label={
                                isHidden
                                  ? t("password_gen_show_aria")
                                  : t("password_gen_hide_aria")
                              }
                              aria-pressed={!isHidden}
                              title={
                                isHidden
                                  ? t("password_gen_show_aria")
                                  : t("password_gen_hide_aria")
                              }
                              className="p-1.5 rounded-md text-indigo-700 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                              {isHidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            </button>
                          )}
                          <StandardButton
                            onClick={() => copy(pw, t("password_gen_password_label"))}
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("copy_button")}
                          </StandardButton>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("password_gen_note_crypto")}</p>
              <p>{t("password_gen_note_entropy")}</p>
              <p>{t("password_gen_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default PasswordGenerator;
